'use client';

import Box from '@enlighten/design-system/dist/Box';
import Button from '@enlighten/design-system/dist/Button';
import Checkbox from '@enlighten/design-system/dist/Checkbox';
import CircularProgress from '@enlighten/design-system/dist/CircularProgress';
import FormControlLabel from '@enlighten/design-system/dist/FormControlLabel';
import FormHelperText from '@enlighten/design-system/dist/FormHelperText';
import Grid from '@enlighten/design-system/dist/Grid';
import Select from '@enlighten/design-system/dist/Select';
import TextField from '@enlighten/design-system/dist/TextField';
import Typography from '@enlighten/design-system/dist/Typography';
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import NewsLetterBackgroundImage from '@/assets/images/pages/home/newsletter/img_bg.png';
import type { Occupation } from '@/constants/news-letter';
import { occupationList } from '@/constants/news-letter';
import { useOnChangeMessage } from '@/contexts/layouts/MessageProvider';

interface FormDataType {
	email: string;
	occupation: Occupation;
	etcInput?: string;
	agree: boolean;
}

export default function Section5() {
	const [loading, setLoading] = useState<boolean>(false);

	const onChangeMessage = useOnChangeMessage();

	const { control, watch, setValue, handleSubmit } = useForm<FormDataType>({ mode: 'all' });

	useEffect(
		() =>
			watch((_, { name, type }) => {
				if (type === 'change') {
					switch (name) {
						case 'occupation':
							setValue('etcInput', undefined);
							break;
						default:
							break;
					}
				}
			}).unsubscribe,
		[watch, setValue]
	);

	const onSubmit = async (formData: FormDataType) => {
		setLoading(true);
		try {
			const response = await fetch('/api/news-letter', {
				method: 'POST',
				body: JSON.stringify({
					data: {
						email: formData.email,
						occupation: formData.occupation === 'etc' ? formData.etcInput || '' : formData.occupation,
						mailingType: 'newsletter',
					},
				}),
			}).then((response) => response.json());

			if (response.status >= 200 && response.status < 300) {
				onChangeMessage({
					type: 'success',
					message: response.message,
					duration: 4000,
				});
			} else {
				throw response;
			}
		} catch (e: any) {
			onChangeMessage({
				type: 'error',
				message: e.message,
				duration: 4000,
			});
		} finally {
			setLoading(false);
		}
	};

	return (
		<Grid container justifyContent="center" bgcolor="grey.100">
			<Grid container direction="column" flex={1} gap={3} maxWidth={{ md: 720 }} padding="85px 24px 95px">
				<Typography fontSize={{ xs: 24, md: 34 }} fontWeight={700}>
					{'놓치면 안 될 에너지 소식,\n매주 받아보세요!'}
				</Typography>
				<Grid container direction="column">
					<Controller
						control={control}
						name="email"
						rules={{
							required: '이메일을 입력해 주세요',
							pattern: {
								value: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
								message: '올바른 이메일을 입력해 주세요',
							},
						}}
						render={({ field: { value, onChange }, fieldState: { error } }) => (
							<TextField
								variant="outlined"
								placeholder="이메일을 입력해 주세요"
								value={value || ''}
								onChange={onChange}
								error={!!error}
								helperText={error?.message}
								slotProps={{
									input: {
										sx: { fontSize: 16 },
									},
								}}
								sx={{
									'.MuiInputBase-root': {
										bgcolor: 'common.white',
										marginBottom: 1,
										'&.Mui-error': {
											marginBottom: 0,
										},
									},
								}}
							/>
						)}
					/>
					<Box width="100%" height={4} />
					<Controller
						control={control}
						name="occupation"
						rules={{
							required: '직업을 선택해 주세요',
						}}
						render={({ field: { value, onChange }, fieldState: { error } }) => (
							<>
								<Select
									displayEmpty
									renderValue={(selected) => (selected ? occupationList.find((item) => item.value === selected)?.label : '어떤 일을 하고 계신가요?')}
									value={value || ''}
									onChange={onChange}
									error={!!error}
									IconComponent={ExpandMoreOutlined}
									items={occupationList.map(({ label, value }) => ({
										title: label,
										value,
										sx: { fontSize: 16 },
									}))}
									sx={{
										fontSize: 16,
										bgcolor: 'common.white',
										color: !!value ? 'common.black' : '#0000005D',
										marginBottom: 1,
										'&.Mui-error': {
											marginBottom: 0,
										},
									}}
								/>
								{value === 'etc' && (
									<Controller
										control={control}
										name="etcInput"
										rules={{
											required: '직업을 입력해 주세요',
										}}
										render={({ field: { value: etcInput, onChange: onChangeEtcInput }, fieldState: { error } }) => (
											<TextField
												placeholder="어떤 일을 하는지 입력해 주세요"
												value={etcInput || ''}
												onChange={(e) => {
													onChangeEtcInput(e.target.value);
												}}
												error={!!error}
												helperText={error?.message}
												slotProps={{ input: { sx: { fontSize: 16 } } }}
											/>
										)}
									/>
								)}
								{error && <FormHelperText error>{error.message}</FormHelperText>}
							</>
						)}
					/>
					<Box width="100%" height={4} />
					<Controller
						control={control}
						name="agree"
						rules={{
							required: '약관에 동의해 주세요',
						}}
						render={({ field: { value, onChange }, fieldState: { error } }) => (
							<>
								<Grid container alignItems="center">
									<FormControlLabel
										control={<Checkbox size="small" />}
										value={value}
										onChange={(_, checked) => {
											onChange(checked);
										}}
										label="개인정보 수집•이용에 동의합니다."
										labelPlacement="end"
										sx={{
											marginX: 0,
											'.MuiFormControlLabel-label': {
												color: 'grey.A200',
												paddingX: 1,
											},
										}}
									/>
									<Link href="https://privacy.enlighten.kr/b950f03f-3434-4c34-8a67-13657d724408" target="_blank">
										<Typography
											color="grey.A200"
											sx={{
												textDecoration: 'underline',
												transition: 'color 0.3s ease',
												':hover': {
													color: 'primary.light',
													textDecoration: 'none',
												},
											}}
										>
											약관보기
										</Typography>
									</Link>
								</Grid>
								{error && <FormHelperText error>{error.message}</FormHelperText>}
							</>
						)}
					/>
				</Grid>
				<Grid container>
					<Button type="submit" onClick={handleSubmit(onSubmit)}>
						{loading && <CircularProgress size={14} color="info" sx={{ color: 'common.white', marginRight: 2 }} />}
						뉴스레터 구독하기
					</Button>
				</Grid>
			</Grid>
			<Grid
				display={{ xs: 'none', md: 'block' }}
				container
				flex={1}
				maxWidth={720}
				sx={{
					background: `url(${NewsLetterBackgroundImage.src}) center center / 100% no-repeat`,
				}}
			/>
		</Grid>
	);
}
